import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import { createFlashSalesConfig } from '@/app/common/components/Promotions/FlashSalesCounter/utils.ts';
import { getCardHref } from '@/app/components/search/utils/buildDealCards/createHotelDetailDeeplink.ts';
import { Icon } from '@/app/common/icons/Icon';
import GalleryItem from './GalleryItem';
import { CloseButtonContainer, GalleryContainer, ImageLoader, TshBackdrop, TshLogo } from './styled';
import type { DealCard } from '../../../utils/buildDealCards/dealCardTypes';
import { CardsContext } from '../../SearchPageContainer/ListContainer/CardsGrid/CardsContext';
import { type HandleDealCardCtaCallBack } from '../types';
import Carousel from './Carousel';
import BadgeSlot from '../../EvolutionCard/Badges/BadgeSlot.tsx';
import { BADGE_SLOTS } from '../../EvolutionCard/Badges/types';
import { HOTEL_THEME, KIDS_STAY_FREE, WELCOME_GIFT_OFFER } from '../../EvolutionCard/Badges/localPromotionTypes';
import { hasPromotion } from '../utils/getPromotionsUtils';
import hotelImagesEvent from '../../../Curiosity/hotelImagesEvent';
import { HotelImageActionTypeEnum } from '../../../Curiosity/Analytics/utils/HotelImageActionTypeEnum';
import useOnScreenWithRefCallback from '../../ShowMore/useOnScreenWithRefCallback';
import { isLiveSearchForMemsearch } from '../../../utils/api/utils';
import getSearchParams from '../../../common/getSearchParams';
import { ClickEventNameEnum } from '../../../Curiosity/Analytics/utils/ClickEventNameEnum';
import rtcoClientViewHotel from '../../../rtco/rtcoClientViewHotel';
import AddToComparison from './AddToComparison';

const timeOnScreenToBeCountedAsVisible = 2000;

type ImageSeenBySearch = {
  [vcSearchId: string]: {
    [hotelId: string]: number[];
  };
};
const imageSeenBySearch: ImageSeenBySearch = {};

const hasImageBeenSeen = (hotelId: number, imageIndex: number): boolean => {
  const searchParams = getSearchParams();
  return imageSeenBySearch[searchParams[URLParameters.VC_SEARCH_ID]]?.[hotelId.toString()]?.indexOf(imageIndex) >= 0;
};

const registerImageSeen = (hotelIdNum: number, imageIndex: number): void => {
  const hotelId = hotelIdNum?.toString();
  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.VC_SEARCH_ID] || searchParams[URLParameters.SEARCH_ID] || '999';
  const imagesBySearch = imageSeenBySearch[searchId];
  if (imagesBySearch && hotelId) {
    const hotelById = imagesBySearch[hotelId];
    if (hotelById) {
      if (hotelById.indexOf(imageIndex) < 0) {
        hotelById.push(imageIndex);
      }
    } else {
      imagesBySearch[hotelId] = [imageIndex];
    }
  } else {
    imageSeenBySearch[searchId] = { [hotelId]: [imageIndex] };
  }
};

type GalleryProps = {
  dealCard?: DealCard;
  isOverlay: boolean;
  isNotClickable: boolean;
  promotionTypes: PROMOTION_TYPES[];
  handleDealCardCtaCallBack: HandleDealCardCtaCallBack;
  debugEnabled: boolean;
  onClose?: () => void;
};

const Gallery = memo(
  ({
    dealCard,
    isOverlay,
    isNotClickable,
    promotionTypes,
    debugEnabled,
    handleDealCardCtaCallBack,
    onClose,
  }: GalleryProps) => {
    const { searchInfoData, localization, curiosityParams, commonData } = usePageWrapperData();
    const { isMobile, isDesktop } = useCommonClient();
    const { isWaiting, isLoading } = useContext(CardsContext);
    const isApp = commonData?.isApp;
    const { t } = useTranslation();
    const selectedItemRef = useRef<number | undefined>(undefined);
    const isVisibleRef = useRef<boolean>(false);
    const [selectedItem, setSelectedItem] = useState(() => 0);
    const searchParams = getSearchParams();
    const isLiveSearch = searchInfoData?.isLiveSearch;
    const comparisonEnabled =
      localization?.configValuesApp?.comparisonEnabled && isLiveSearch && !isWaiting && !isLoading;
    const hotelId = dealCard?.product?.accommodation?.hotelId;

    useEffect(() => {
      if (isLoading || isWaiting) {
        setSelectedItem(0);
      }
    }, [isLoading, isWaiting]);

    const tmVisible = useRef<number | undefined>(undefined);

    const [galleryContainerRef, domNode, isVisible] = useOnScreenWithRefCallback({ options: { threshold: 1 } });

    const clearTm = () => {
      if (tmVisible.current) {
        window.clearTimeout(tmVisible.current);
        tmVisible.current = undefined;
      }
    };

    const fireHotelImagesEvent = (actionType: HotelImageActionTypeEnum, imageIndex: number) => {
      if (
        dealCard &&
        hotelId &&
        isLiveSearch &&
        !dealCard.product?.dates?.soldOut &&
        !isWaiting &&
        !isLoading &&
        !isMobile &&
        !isApp
      ) {
        hotelImagesEvent({
          actionType,
          dealCard,
          imageIndex: imageIndex + 1,
          commonData,
          localization,
          curiosityParams,
          AbortController,
        });

        if (actionType === HotelImageActionTypeEnum.SWIPE) {
          const imageIndexSeen = imageIndex + 1;
          if (!hasImageBeenSeen(hotelId, imageIndexSeen)) {
            registerImageSeen(hotelId, imageIndexSeen);
            fireHotelImagesEvent(HotelImageActionTypeEnum.SEEN, imageIndexSeen);
          }
        }
        if (actionType === HotelImageActionTypeEnum.SWIPE_BACK) {
          const imageIndexSeen = imageIndex - 1;
          if (!hasImageBeenSeen(hotelId, imageIndexSeen)) {
            registerImageSeen(hotelId, imageIndexSeen);
            fireHotelImagesEvent(HotelImageActionTypeEnum.SEEN, imageIndexSeen);
          }
        }
      }
    };

    useEffect(
      () => () => {
        clearTm();
      },
      []
    );

    useEffect(() => {
      if (isOverlay) {
        setSelectedItem(0);
      }
    }, [isOverlay]);

    useEffect(() => {
      selectedItemRef.current = selectedItem;
    }, [selectedItem]);

    useEffect(() => {
      isVisibleRef.current = isVisible;
      if (hotelId && isVisible && !isWaiting && !isLoading) {
        clearTm();
        tmVisible.current = window.setTimeout(() => {
          if (isVisibleRef.current) {
            const hotelHasBeenSeen = hasImageBeenSeen(hotelId, 0);
            if (isLiveSearchForMemsearch({ searchInfoData }) && !hotelHasBeenSeen) {
              // Only send the RTCO event if we have the position of the result in the list. We don't have it for example
              // for the highlighted hotel on the detail page map.
              if (dealCard?.product?.debug?.rankingPosOriginal) {
                rtcoClientViewHotel({
                  searchParams,
                  searchInfoData,
                  localization,
                  hotelId,
                  position: dealCard?.product?.debug?.rankingPosOriginal,
                  commonData,
                });
              }
            }
            if (!hasImageBeenSeen(hotelId, selectedItem) && selectedItemRef.current) {
              registerImageSeen(hotelId, selectedItem);
              fireHotelImagesEvent(HotelImageActionTypeEnum.SEEN, selectedItemRef.current);
            }
          }
        }, timeOnScreenToBeCountedAsVisible);
      } else {
        clearTm();
      }
    }, [isVisible, isWaiting, isLoading]);

    if ((isLoading && !isWaiting) || !dealCard?.product?.accommodation) {
      return <ImageLoader isOverlay={isOverlay} />;
    }

    const deepLink = getCardHref({ dealCard, searchInfoData, commonData });
    const { name, image, gallery } = dealCard.product.accommodation;
    const viewMore = t('vacation.oss.cards.view_more');
    const images = [...new Set([image, ...gallery])].filter(Boolean);
    // The map overlay card deliberately shows a single image: it renders neither arrows nor dots
    // (see showArrows/showDots below), so the rest only cost a download each.
    const filteredImages = (isOverlay ? images.slice(0, 1) : images).map((x, i) => ({
      id: i,
      hotelId,
      image: x,
      isOverlay,
      isMobile,
      isNotClickable,
      isLast: i === images.length - 1 && images.length !== 1,
      isFirst: i === 0,
      viewMore,
    }));
    const hasFlashSales = hasPromotion(PROMOTION_TYPES.FLASH_SALES, promotionTypes);
    const hasJanuarySales = hasPromotion(PROMOTION_TYPES.JANUARY_SALE, promotionTypes);
    const hasTsh = hasPromotion(PROMOTION_TYPES.TOP_SECRET_HOTELS, promotionTypes);
    const hasFlashSalesCounter = hasFlashSales || hasJanuarySales;
    const flashSalesConfig = hasFlashSalesCounter ? createFlashSalesConfig(dealCard, t) : undefined;
    const showFlashSalesCounter = flashSalesConfig?.isActive && !isOverlay && !isWaiting;
    const hasNoImages = filteredImages.length === 0;
    const showThemeBadges = !isOverlay && !hasTsh;
    const themeBadgeTypes = [HOTEL_THEME, KIDS_STAY_FREE, WELCOME_GIFT_OFFER];
    const galleryBadgeTypes = [
      ...(showFlashSalesCounter ? [PROMOTION_TYPES.FLASH_SALES] : []),
      ...(showThemeBadges ? promotionTypes.filter((type) => themeBadgeTypes.includes(type)) : []),
    ];
    const showCarousel = !isMobile;
    const showArrows = !isOverlay && !isMobile && !hasNoImages;
    const showDots = showArrows;

    const handleOnScroll = () => {
      if (domNode) {
        const { width } = domNode.getBoundingClientRect();
        const { scrollLeft } = domNode;
        const itemToSelect = Math.round(scrollLeft / width);

        if (selectedItem !== itemToSelect) {
          if (itemToSelect > selectedItem) {
            fireHotelImagesEvent(HotelImageActionTypeEnum.SWIPE, selectedItem);
          }
          if (itemToSelect < selectedItem) {
            fireHotelImagesEvent(HotelImageActionTypeEnum.SWIPE_BACK, selectedItem);
          }
          setSelectedItem(itemToSelect);
        }
      }
    };

    const handleGalleryClicked = (
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      handleDealCardCtaCallBack({ e, payload: { event_name: ClickEventNameEnum.hotelCardClick } });
    };

    const handleImageClicked = (imageIndex: number) => {
      fireHotelImagesEvent(HotelImageActionTypeEnum.CLICK, imageIndex);
    };

    const showCloseButton = isOverlay && onClose;
    const isDark = !isDesktop;

    return (
      <>
        {showCloseButton && (
          <CloseButtonContainer
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onClose?.();
            }}
            isDark={isDark}
            isSmall={!isDesktop}
          >
            <Icon
              category="Semantics"
              name="icon-cross"
              size={24}
              color={isDark ? 'iconOnDark' : 'iconPrimary'}
              alt={t('vacation.oss.cards.map.close')}
            />
          </CloseButtonContainer>
        )}
        {showCarousel && (
          <Carousel
            selectedItem={selectedItem}
            galleryContainerNode={domNode}
            itemsLength={filteredImages.length}
            isOverlay={isOverlay}
            handleDealCardCtaCallBack={handleDealCardCtaCallBack}
            showArrows={showArrows}
            showDots={showDots}
            fullWidthOnMobile={false}
          />
        )}
        {comparisonEnabled && !isOverlay && <AddToComparison dealCard={dealCard} />}
        <GalleryContainer
          ref={galleryContainerRef}
          onScroll={handleOnScroll}
          itemsLength={filteredImages.length}
          className="card-gallery"
          isOverlay={isOverlay}
          debugEnabled={debugEnabled}
          data-testid="gallery"
          noImage={hasNoImages}
          {...(isNotClickable
            ? {}
            : { as: 'a', href: deepLink, target: '_blank', onClick: handleGalleryClicked, 'aria-label': name })}
        >
          {hasTsh && (
            <>
              <TshLogo
                src={`${localization.imageBasePath}icons/top_secret_hotel.svg`}
                alt="top secret hotel"
                width={111}
                height={90}
              />
              <TshBackdrop />
            </>
          )}
          {filteredImages.map((galleryItem, index) => (
            <GalleryItem
              key={galleryItem.id}
              imageIndex={index}
              {...galleryItem}
              handleDealCardCtaCallBack={handleDealCardCtaCallBack}
              handleImageClicked={handleImageClicked}
              imagesCount={filteredImages.length}
              fullWidthOnMobile={false}
            />
          ))}
        </GalleryContainer>
        {galleryBadgeTypes.length > 0 && (
          <BadgeSlot
            name={BADGE_SLOTS.GALLERY_OVERLAY_OLD}
            dealCard={dealCard}
            promotionTypes={galleryBadgeTypes}
            isOverlay={isOverlay}
          />
        )}
      </>
    );
  },
  (prevProps: GalleryProps, nextProps: GalleryProps) => {
    if (JSON.stringify(prevProps.dealCard) !== JSON.stringify(nextProps.dealCard)) return false;
    if (prevProps.isNotClickable !== nextProps.isNotClickable) return false;
    return true;
  }
);

Gallery.displayName = 'Gallery';

export default Gallery;
